import { ColorTypes } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { CellModel, CellSource, ColumnSource, RequestFilters, RowSource } from "@omegagrid/grid";
import { ToolbarItem } from "@omegagrid/toolbar";
import rp, { GridEditorAdapter, GridEditorOptions, RowData, createScheduleHtml } from "@reporting/rp-app-host";
import { ReportEmail } from "@reporting/rp-types/accounting";

const PAGE_SIZE = 500;

export class EmailGridEditorAdapter extends GridEditorAdapter {

	private _columns: ColumnSource[];

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;
		this._columns = [{
			data: 'controls',
			t: 'a',
			e: false,
			w: 5 + 20 * 5,
			content: null,
			filter: false
		}, {
			data: 'company',
			header: msg('Company'),
			t: 't',
			w: 200,
			levelButtons: true,
			levelIndent: true,
		}, {
			data: 'title',
			header: 'ID',
			t: 't',
			w: 120,
		}, {
			data: 'description',
			header: msg('Report'),
			t: 't',
			w: 180,
		}, {
			data: 'recipient',
			header: msg('Recipients'),
			t: 't',
			w: 220,
			filter: false,
		}, {
			data: 'subject',
			header: msg('Subject'),
			t: 't',
			w: 200,
		}, {
			data: 'filename',
			header: msg('File'),
			t: 't',
			w: 100,
		}, {
			data: 'schedule',
			header: msg('Schedule'),
			t: 't',
			w: 350,
			filter: false,
		}, {
			data: 'idz',
			t: 'n',
			hidden: true,
		}, {
			data: 'accounting',
			t: 'n',
			hidden: true,
		}, {
			data: 'id_embedding',
			t: 't',
			hidden: true,
		}];
		return this._columns;
	}

	async getRows(filters?: RequestFilters): Promise<RowSource[]> {
		const rows: RowSource[] = [];
		const emails = await rp.ms('accounting').get<ReportEmail[]>('auto/emails', this.createRequestParams(filters, {
			count: PAGE_SIZE
		}));
		let prev;
		for (const email of emails) {
			if (!prev || prev.accounting !== email.accounting) {
				rows.push({ l: 0, data: {
					controls: { content: this.getRowControls() },
					company: { cs: 7, v: email.company },
					accounting: email.accounting,
				}});
			}
			if (email.idz) {
				rows.push({ l: 1, h: 80, s: { alignment: { vertical: 'center' } }, data: {
						...email,
						controls: { content: this.getRowControls(email) },
						company: email.company,
						title: email.id_embedding ?? email.id_thread,
						recipient: this.createRecipientsHtml(email),
						schedule: this.createScheduleHtml(email),
					},
				});
			}
			prev = email;
		}
		return rows;
	}

	getRowControls(email: ReportEmail = null) {
		if (!email) {
			return [{
				action: 'addEmail',
				title: msg('Add'),
				icon: 'plus',
				color: ColorTypes.Blue,
			}];
		}
		return [{
			action: 'delete',
			title: msg('Delete'),
			icon: 'trash',
			color: ColorTypes.Red,
		}, {
			action: 'duplicateEmail',
			title: msg('Duplicate'),
			icon: 'copy',
			color: ColorTypes.Yellow
		}, {
			action: 'editEmail',
			title: msg('Edit'),
			icon: 'pen',
			color: ColorTypes.Gray,
		}, {
			action: 'sendEmail',
			title: msg('Send email'),
			icon: 'paper-plane',
			color: ColorTypes.Green,
		}, email.id_embedding ? {
			action: 'previewEmail',
			title: msg('Preview email'),
			icon: 'eye',
			color: ColorTypes.Accent,
		} : {}];
	}

	createRecipientsHtml(email: ReportEmail): Partial<CellSource> {
		let html = '<div>';
		if (email.recipient) {
			html += `${email.recipient.replace(/(?:\r\n|\r|\n)/g, "<br />")}`;
		}
		if (email.copy_recipient) {
			html += `<br><b>${msg('CC')}:</b> ${email.copy_recipient.replace(/(?:\r\n|\r|\n)/g, "<br />")}`;
		}
		if (email.blind_copy_recipient) {
			html += `<br><b>${msg('BCC')}:</b> ${email.blind_copy_recipient.replace(/(?:\r\n|\r|\n)/g, "<br />")}`;
		}
		html += '</div>';
		return { t: 'h', v: html };
	}

	createScheduleHtml(email: ReportEmail): Partial<CellSource> {
		return createScheduleHtml(email);
	}

	async save(rowData: RowData[]) {
		for (const row of rowData) {
			if (row.state === 'deleted') {
				const idz = row.data.idz as number;
				if (idz) await rp.ms('accounting').delete(`auto/emails/${idz}`, { accounting: row.data.accounting as number });
			}
		}
	}

	getOptions(): Partial<GridEditorOptions> {
		return this.createOptions({
			editable: false,
			pageSize: PAGE_SIZE,
			refreshAfterSave: true,
			filters: true,
			colLetters: false,
			rowNumbers: false,
			columnSorting: true,
			rowTree: false,
			columnHeaderTreeButtons: true,
			levelButtonCell: { r: -2, c: 0 },
			lightTheme: null,
			darkTheme: null,
			toolbar: {
				new: {enabled: false},
			},
		});
	}

	async onAction(action: string, cell: CellModel): Promise<void> {
		switch (action) {
			case 'addEmail':
				rp.host.openModalSidebar({
					id: `admin-emailForm-${cell.row.data.accounting}`,
					caption: msg('New email')
				});
				break;
			case 'editEmail':
				rp.host.openModalSidebar({
					id: `admin-emailForm-${cell.row.data.accounting}-${cell.row.data.idz}`,
					caption: msg('Edit email')
				});
				break;
			case 'duplicateEmail':
				rp.host.openModalSidebar({
					id: `admin-emailForm-${cell.row.data.accounting}-${cell.row.data.idz}-copy`,
					caption: msg('Duplicate')
				});
				break;
			case 'sendEmail':
				const command = await rp.host.openMenu({
					content: 'yes_no',
					text: msg('Are you sure you want to send this email?'),
				});
				if (command?.id == 'Y') {
					const result = await rp.ms('accounting').post(`auto/emails/${cell.row.data.idz}/send`, null, { accounting: cell.row.data.accounting as number });
					if (result) {
						rp.host.bucket.showAlert({ content: msg('Email sent successfully.'), type: 'success' });
					} else {
						rp.host.bucket.showAlert({ content: msg('Failed to send email.'), type: 'danger' });
					}
				}
				break;
			case 'previewEmail':
				// TODO
				break;
		}
	}

}
